// The framed viewfinder box, defined once so the visual mask (ScanBox/ScannerScreen) and the
// decode crop (useScanLoop) can never drift apart — if they did, we'd decode a region the
// attendee didn't aim the barcode at. Ported from event-connect's scanner (viewfinder.ts); the
// fractions are re-tuned below to match this step's own viewport mask (product-scan.module.scss
// `.viewportWrap`/scan corners), not event-connect's `w-[68%] h-[38%]` box.
//
// Cropping the decode to this box (instead of the whole camera frame) is the whole point:
// fewer pixels per zxing pass, and the barcode fills more of the ones that remain.
// `roiToFrame`/`displayToFrame` do the actual coordinate math; this module only assembles the
// rect and clamps it.

import { displayToFrame } from './geometry'
import type { Rect, Size } from './decoder/types'

/** Centered viewfinder box as a fraction of the display — matches `.scanCorners { inset: 34% 5% }`
 * in product-scan.module.scss (the visible bracket frame), not event-connect's own mask
 * fractions, which belong to a different overlay design this step doesn't use.
 * Wide band (BR-5571 §3.5/3.7): under `object-fit: cover`, CSS box size doesn't move the decode
 * floor, only aim-target shape does — a wide band raises expected px/module ~2.27 → ~5.39 at 720p
 * versus the old centered square. The fractions and `.scanCorners` inset MUST change together. */
export const VIEWFINDER_WIDTH_FRACTION = 0.9
export const VIEWFINDER_HEIGHT_FRACTION = 0.32

const clamp = (value: number, max: number) => Math.min(Math.max(value, 0), max)

/**
 * The frame-space (camera-pixel) rectangle the decoder should crop to — the on-screen
 * viewfinder box mapped back through the video's `object-fit: cover`. Corners are clamped
 * to the frame (cover crops asymmetrically, so a display corner can map outside the frame)
 * and rounded to whole pixels for a clean `drawImage` crop.
 */
export function viewfinderFrameRect(frameSize: Size, displaySize: Size): Rect {
  const displayRect = viewfinderDisplayRect(displaySize)
  const topLeft = displayToFrame(displayRect, frameSize, displaySize, 'cover')
  const bottomRight = displayToFrame(
    { x: displayRect.x + displayRect.width, y: displayRect.y + displayRect.height },
    frameSize,
    displaySize,
    'cover',
  )

  const x = clamp(topLeft.x, frameSize.width)
  const y = clamp(topLeft.y, frameSize.height)
  const right = clamp(bottomRight.x, frameSize.width)
  const bottom = clamp(bottomRight.y, frameSize.height)

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(right - x),
    height: Math.round(bottom - y),
  }
}

/** The centered viewfinder box in display (CSS) space — the same rect the mask draws. */
function viewfinderDisplayRect(displaySize: Size): Rect {
  const width = displaySize.width * VIEWFINDER_WIDTH_FRACTION
  const height = displaySize.height * VIEWFINDER_HEIGHT_FRACTION
  return {
    x: (displaySize.width - width) / 2,
    y: (displaySize.height - height) / 2,
    width,
    height,
  }
}
